/**
 * Gradient model shared by every fill in the publication (shape fill, text fill, text box background, page background,
 * table cells, WordArt) and by the three ways a fill is painted: CSS (`gradientToCss`), SVG paths (`GradientSvgDefs`)
 * and exports (the browser paints both, so they match).
 *
 *   gradient = {
 *     type:   'linear' | 'radial' | 'conic',
 *     angle:  0..360      linear direction (CSS: 0 = up, 90 = right, clockwise) / conic start angle,
 *     stops:  [{ color: '#rrggbb', position: 0..100, opacity: 0..100 }]  (2..12 stops, any order — sorted for painting),
 *     center: { x: 0..100, y: 0..100 }   radial & conic centre (percent of the box),
 *     shape:  'circle' | 'ellipse'       radial only,
 *     size:   'farthest-corner' | 'closest-side' | 'farthest-side' | 'closest-corner'   radial extent,
 *   }
 * Older publications only carry { type, angle, stops:[{color, position?}] } — `normalizeGradient` fills in the rest.
 */
import React from 'react';
import { normalizeHex, hexToRgb, rgbToHex, hexWithAlpha, tint, shade, clamp } from './colorMath';

export const GRADIENT_TYPES = [
  { id: 'linear', label: 'Linear' },
  { id: 'radial', label: 'Radial' },
  { id: 'conic', label: 'Angular' },
];
export const RADIAL_SIZES = [
  { id: 'farthest-corner', label: 'To farthest corner' },
  { id: 'farthest-side', label: 'To farthest side' },
  { id: 'closest-side', label: 'To closest side' },
  { id: 'closest-corner', label: 'To closest corner' },
];
export const MAX_STOPS = 12;

const num = (v, fallback) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
const pct = (v, fallback) => clamp(num(v, fallback), 0, 100);

export const normalizeStop = (s, i, n) => ({
  color: normalizeHex(s?.color || '#7c3aed'),
  position: pct(s?.position, n > 1 ? Math.round((i / (n - 1)) * 100) : 0),
  opacity: pct(s?.opacity, 100),
});

/** A complete gradient object (defaults filled in, stops sorted by position, at least two stops). */
export const normalizeGradient = (g) => {
  const src = g && typeof g === 'object' ? g : {};
  let stops = Array.isArray(src.stops) && src.stops.length ? src.stops.map((s, i) => normalizeStop(s, i, src.stops.length)) : [];
  if (stops.length === 0) stops = [normalizeStop({ color: '#7c3aed', position: 0 }, 0, 2), normalizeStop({ color: '#c4b5fd', position: 100 }, 1, 2)];
  if (stops.length === 1) stops = [stops[0], { ...stops[0], position: 100 }];
  stops = [...stops].sort((a, b) => a.position - b.position);
  const type = ['linear', 'radial', 'conic'].includes(src.type) ? src.type : 'linear';
  return {
    type,
    angle: ((num(src.angle, type === 'radial' ? 0 : 90) % 360) + 360) % 360,
    stops: stops.slice(0, MAX_STOPS),
    center: { x: pct(src.center?.x, 50), y: pct(src.center?.y, 50) },
    shape: src.shape === 'ellipse' ? 'ellipse' : 'circle',
    size: RADIAL_SIZES.some((s) => s.id === src.size) ? src.size : 'farthest-corner',
  };
};

export const DEFAULT_GRADIENT = normalizeGradient({ type: 'linear', angle: 90, stops: [{ color: '#7c3aed', position: 0 }, { color: '#c4b5fd', position: 100 }] });

/** A first gradient built from a solid colour (light tint → the colour), so switching a fill to "Gradient" keeps its look. */
export const gradientFromColor = (hex, angle = 180) => {
  const base = normalizeHex(hex || '#7c3aed');
  const flat = base === '#ffffff' || base === '#000000';
  return normalizeGradient({ type: 'linear', angle, stops: [{ color: flat ? '#c4b5fd' : tint(base, 0.55), position: 0 }, { color: flat ? '#7c3aed' : shade(base, 0.15), position: 100 }] });
};

/** `{ type: 'gradient', gradient }` fill object. */
export const gradientFill = (gradient) => ({ type: 'gradient', gradient: normalizeGradient(gradient) });

/* ------------------------------------------------------------------ */
/* Colour helpers                                                      */
/* ------------------------------------------------------------------ */

/** CSS colour of one stop (rgba when it is not fully opaque). */
export const stopCss = (stop) => (stop.opacity >= 100 ? stop.color : hexWithAlpha(stop.color, stop.opacity));

/** Colour + opacity the gradient shows at `position` (0..100) — used to insert a new stop without a visible jump. */
export const colorAt = (gradient, position) => {
  const { stops } = normalizeGradient(gradient);
  const p = clamp(position, 0, 100);
  if (p <= stops[0].position) return { color: stops[0].color, opacity: stops[0].opacity };
  const last = stops[stops.length - 1];
  if (p >= last.position) return { color: last.color, opacity: last.opacity };
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (p >= a.position && p <= b.position) {
      const t = b.position === a.position ? 0 : (p - a.position) / (b.position - a.position);
      const ca = hexToRgb(a.color), cb = hexToRgb(b.color);
      return {
        color: rgbToHex({ r: ca.r + (cb.r - ca.r) * t, g: ca.g + (cb.g - ca.g) * t, b: ca.b + (cb.b - ca.b) * t }),
        opacity: Math.round(a.opacity + (b.opacity - a.opacity) * t),
      };
    }
  }
  return { color: last.color, opacity: last.opacity };
};

/* ------------------------------------------------------------------ */
/* CSS                                                                 */
/* ------------------------------------------------------------------ */

const stopsCss = (stops) => stops.map((s) => `${stopCss(s)} ${round1(s.position)}%`).join(', ');
const round1 = (v) => Math.round(v * 10) / 10;

/** The CSS `background-image` for a gradient (also the preview everywhere in the UI). */
export const gradientToCss = (gradient) => {
  const g = normalizeGradient(gradient);
  if (g.type === 'radial') return `radial-gradient(${g.shape} ${g.size} at ${round1(g.center.x)}% ${round1(g.center.y)}%, ${stopsCss(g.stops)})`;
  if (g.type === 'conic') return `conic-gradient(from ${round1(g.angle)}deg at ${round1(g.center.x)}% ${round1(g.center.y)}%, ${stopsCss(g.stops)})`;
  return `linear-gradient(${round1(g.angle)}deg, ${stopsCss(g.stops)})`;
};

/** The gradient laid out flat, left → right — the editor's stop bar. */
export const gradientBarCss = (gradient) => `linear-gradient(90deg, ${stopsCss(normalizeGradient(gradient).stops)})`;

/* ------------------------------------------------------------------ */
/* Editing operations (all return a new normalised gradient)           */
/* ------------------------------------------------------------------ */

export const updateGradient = (gradient, patch) => normalizeGradient({ ...normalizeGradient(gradient), ...patch });

export const updateStop = (gradient, index, patch) => {
  const g = normalizeGradient(gradient);
  return normalizeGradient({ ...g, stops: g.stops.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
};

/** Insert a stop at `position` with the colour the gradient already shows there. Returns { gradient, index }. */
export const addStop = (gradient, position) => {
  const g = normalizeGradient(gradient);
  if (g.stops.length >= MAX_STOPS) return { gradient: g, index: -1 };
  const p = clamp(Math.round(position), 0, 100);
  const stop = { ...colorAt(g, p), position: p };
  const next = normalizeGradient({ ...g, stops: [...g.stops, stop] });
  return { gradient: next, index: next.stops.findIndex((s) => s === stop || (s.position === stop.position && s.color === stop.color && s.opacity === stop.opacity)) };
};

export const removeStop = (gradient, index) => {
  const g = normalizeGradient(gradient);
  if (g.stops.length <= 2) return g;
  return normalizeGradient({ ...g, stops: g.stops.filter((_, i) => i !== index) });
};

/** Swap the colour order (a → b becomes b → a). */
export const reverseGradient = (gradient) => {
  const g = normalizeGradient(gradient);
  return normalizeGradient({ ...g, stops: g.stops.map((s) => ({ ...s, position: 100 - s.position })) });
};

/** Reflect: the current run is squeezed into 0..50 % and mirrored into 50..100 % (a "reflected" gradient). */
export const mirrorGradient = (gradient) => {
  const g = normalizeGradient(gradient);
  const half = g.stops.map((s) => ({ ...s, position: s.position / 2 }));
  const mirrored = g.stops.map((s) => ({ ...s, position: 100 - s.position / 2 })).filter((s) => s.position !== 50 || !half.some((h) => h.position === 50));
  return normalizeGradient({ ...g, stops: [...half, ...mirrored].slice(0, MAX_STOPS) });
};

/** Spread the stops evenly between 0 and 100 %. */
export const distributeStops = (gradient) => {
  const g = normalizeGradient(gradient);
  const n = g.stops.length;
  return normalizeGradient({ ...g, stops: g.stops.map((s, i) => ({ ...s, position: Math.round((i / (n - 1)) * 100) })) });
};

/** Rotate a linear / conic gradient by `delta` degrees. */
export const rotateGradient = (gradient, delta) => updateGradient(gradient, { angle: normalizeGradient(gradient).angle + delta });

/* ------------------------------------------------------------------ */
/* Presets                                                             */
/* ------------------------------------------------------------------ */

const L = (angle, ...colors) => normalizeGradient({ type: 'linear', angle, stops: colors.map((c, i) => ({ color: c, position: Math.round((i / (colors.length - 1)) * 100) })) });
const R = (colors, center = { x: 50, y: 50 }, shape = 'circle') => normalizeGradient({ type: 'radial', center, shape, stops: colors.map((c, i) => ({ color: c, position: Math.round((i / (colors.length - 1)) * 100) })) });
const C = (angle, ...colors) => normalizeGradient({ type: 'conic', angle, stops: colors.map((c, i) => ({ color: c, position: Math.round((i / (colors.length - 1)) * 100) })) });

/** Curated presets — tasteful multi-stop gradients grouped like Publisher's Gradient gallery. */
export const GRADIENT_PRESETS = [
  { id: 'dusk', name: 'Dusk', gradient: L(135, '#1e1b4b', '#7c3aed', '#f472b6') },
  { id: 'ocean', name: 'Ocean', gradient: L(180, '#0c4a6e', '#0ea5e9', '#bae6fd') },
  { id: 'sunrise', name: 'Sunrise', gradient: L(90, '#f59e0b', '#f97316', '#db2777') },
  { id: 'forest', name: 'Forest', gradient: L(160, '#052e16', '#15803d', '#a3e635') },
  { id: 'peach', name: 'Peach', gradient: L(45, '#fde68a', '#fdba74', '#fb7185') },
  { id: 'lavender', name: 'Lavender', gradient: L(135, '#c4b5fd', '#e9d5ff', '#fbcfe8') },
  { id: 'mint', name: 'Mint', gradient: L(135, '#a7f3d0', '#67e8f9') },
  { id: 'steel', name: 'Steel', gradient: L(180, '#e5e7eb', '#9ca3af', '#4b5563') },
  { id: 'gold', name: 'Gold', gradient: L(90, '#b45309', '#fbbf24', '#fef3c7', '#fbbf24', '#b45309') },
  { id: 'silver', name: 'Silver', gradient: L(90, '#6b7280', '#e5e7eb', '#f9fafb', '#d1d5db', '#6b7280') },
  { id: 'copper', name: 'Copper', gradient: L(90, '#7c2d12', '#ea580c', '#fed7aa', '#ea580c', '#7c2d12') },
  { id: 'midnight', name: 'Midnight', gradient: L(180, '#020617', '#1e293b', '#334155') },
  { id: 'spotlight', name: 'Spotlight', gradient: R(['#ffffff', '#e2e8f0', '#64748b'], { x: 50, y: 40 }) },
  { id: 'ember', name: 'Ember', gradient: R(['#fde68a', '#f97316', '#7c2d12'], { x: 50, y: 60 }) },
  { id: 'aurora', name: 'Aurora', gradient: L(120, '#0f766e', '#22d3ee', '#a78bfa', '#f0abfc') },
  { id: 'berry', name: 'Berry', gradient: L(135, '#4c0519', '#be123c', '#fb7185') },
  { id: 'sky-glow', name: 'Sky Glow', gradient: R(['#fef9c3', '#7dd3fc', '#1d4ed8'], { x: 30, y: 30 }, 'ellipse') },
  { id: 'rainbow', name: 'Colour Wheel', gradient: C(0, '#ef4444', '#f59e0b', '#84cc16', '#06b6d4', '#8b5cf6', '#ef4444') },
  { id: 'pinwheel', name: 'Pinwheel', gradient: C(45, '#1e3a8a', '#93c5fd', '#1e3a8a', '#93c5fd', '#1e3a8a') },
  { id: 'fade-out', name: 'Fade Out', gradient: normalizeGradient({ type: 'linear', angle: 180, stops: [{ color: '#7c3aed', position: 0, opacity: 100 }, { color: '#7c3aed', position: 100, opacity: 0 }] }) },
  { id: 'vignette', name: 'Vignette', gradient: normalizeGradient({ type: 'radial', size: 'farthest-corner', stops: [{ color: '#000000', position: 40, opacity: 0 }, { color: '#000000', position: 100, opacity: 70 }] }) },
];

/** Light + dark two-stop variations of a base colour in Publisher's nine directions (the ribbon's Gradient submenu). */
export const DIRECTIONS = [
  { id: 135, name: 'Diagonal down' }, { id: 180, name: 'Down' }, { id: 225, name: 'Diagonal down-left' },
  { id: 90, name: 'Right' }, { id: 'radial', name: 'From centre' }, { id: 270, name: 'Left' },
  { id: 45, name: 'Diagonal up' }, { id: 0, name: 'Up' }, { id: 315, name: 'Diagonal up-left' },
];
export const schemeGradients = (scheme) => {
  const colors = (scheme || []).filter((c) => c && c.hex && !['#000000', '#ffffff'].includes(c.hex.toLowerCase())).slice(0, 6);
  const out = [];
  colors.forEach((c, i) => {
    out.push({ id: `scheme-light-${i}`, name: `${c.name} — light`, gradient: L(180, tint(c.hex, 0.75), c.hex) });
    out.push({ id: `scheme-dark-${i}`, name: `${c.name} — dark`, gradient: L(180, c.hex, shade(c.hex, 0.55)) });
  });
  for (let i = 0; i + 1 < colors.length; i += 2) out.push({ id: `scheme-pair-${i}`, name: `${colors[i].name} → ${colors[i + 1].name}`, gradient: L(135, colors[i].hex, colors[i + 1].hex) });
  return out;
};

/* ------------------------------------------------------------------ */
/* SVG                                                                 */
/* ------------------------------------------------------------------ */

/** CSS-compatible linear gradient line for a W×H box (pixel space): endpoints so the first / last stops touch the corners. */
export const linearLine = (angle, W, H) => {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.sin(rad), dy = -Math.cos(rad);
  const half = (Math.abs(dx) * W + Math.abs(dy) * H) / 2;
  const cx = W / 2, cy = H / 2;
  return { x1: cx - dx * half, y1: cy - dy * half, x2: cx + dx * half, y2: cy + dy * half };
};

/** CSS-compatible radial ending shape for a W×H box (pixel space): centre + radii. */
export const radialEllipse = (g, W, H) => {
  const cx = (g.center.x / 100) * W, cy = (g.center.y / 100) * H;
  const dxs = [cx, W - cx], dys = [cy, H - cy];
  const corners = [[cx, cy], [W - cx, cy], [cx, H - cy], [W - cx, H - cy]].map(([x, y]) => Math.hypot(x, y));
  let rx, ry;
  if (g.shape === 'circle') {
    const r = g.size === 'closest-side' ? Math.min(...dxs, ...dys) : g.size === 'farthest-side' ? Math.max(...dxs, ...dys) : g.size === 'closest-corner' ? Math.min(...corners) : Math.max(...corners);
    rx = ry = Math.max(0.5, r);
  } else {
    const fx = g.size.startsWith('closest') ? Math.min(...dxs) : Math.max(...dxs);
    const fy = g.size.startsWith('closest') ? Math.min(...dys) : Math.max(...dys);
    const k = g.size.endsWith('corner') ? Math.SQRT2 : 1;
    rx = Math.max(0.5, fx * k); ry = Math.max(0.5, fy * k);
  }
  return { cx, cy, rx, ry };
};

const conicCache = new Map();
/** Angular gradients have no SVG primitive: rasterise one (browser canvas) at the box's aspect ratio. */
export const conicDataUrl = (g, W, H) => {
  const w = Math.max(2, Math.round(Math.min(1024, W * 2))), h = Math.max(2, Math.round((w * H) / Math.max(1, W)));
  const key = `${JSON.stringify(g)}|${w}x${h}`;
  const hit = conicCache.get(key);
  if (hit) return hit;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    let paint;
    if (typeof ctx.createConicGradient === 'function') paint = ctx.createConicGradient(((g.angle - 90) * Math.PI) / 180, (g.center.x / 100) * w, (g.center.y / 100) * h);
    else paint = ctx.createRadialGradient((g.center.x / 100) * w, (g.center.y / 100) * h, 0, (g.center.x / 100) * w, (g.center.y / 100) * h, Math.hypot(w, h) / 2);
    g.stops.forEach((s) => paint.addColorStop(clamp(s.position, 0, 100) / 100, stopCss(s)));
    ctx.fillStyle = paint;
    ctx.fillRect(0, 0, w, h);
    const url = canvas.toDataURL('image/png');
    if (conicCache.size > 60) conicCache.delete(conicCache.keys().next().value);
    conicCache.set(key, url);
    return url;
  } catch {
    return '';
  }
};

/**
 * `<defs>` children painting `gradient` inside an SVG whose user space maps to a W×H pixel box through
 * `x_user = tx + sx · x_px`, `y_user = ty + sy · y_px`. The geometry is computed in pixel space (exactly like CSS) and
 * carried over with `gradientTransform`, so a stretched viewBox (100×100 → any size) keeps angles and circles true.
 * Returns the paint reference too: `url(#id)`.
 */
export const GradientSvgDefs = ({ id, gradient, width, height, tx = 0, ty = 0, sx = 1, sy = 1 }) => {
  const g = normalizeGradient(gradient);
  const W = Math.max(1, width || 100), H = Math.max(1, height || 100);
  const transform = `translate(${tx} ${ty}) scale(${sx} ${sy})`;
  const stops = g.stops.map((s, i) => <stop key={i} offset={`${s.position}%`} stopColor={s.color} stopOpacity={s.opacity / 100} />);
  if (g.type === 'radial') {
    const { cx, cy, rx, ry } = radialEllipse(g, W, H);
    return <radialGradient id={id} gradientUnits="userSpaceOnUse" cx="0" cy="0" r="1" gradientTransform={`${transform} translate(${cx} ${cy}) scale(${rx} ${ry})`}>{stops}</radialGradient>;
  }
  if (g.type === 'conic') {
    const url = conicDataUrl(g, W, H);
    return (
      <pattern id={id} patternUnits="userSpaceOnUse" x={tx} y={ty} width={W * sx} height={H * sy}>
        {url ? <image href={url} width={W * sx} height={H * sy} preserveAspectRatio="none" /> : <rect width={W * sx} height={H * sy} fill={g.stops[0].color} />}
      </pattern>
    );
  }
  const { x1, y1, x2, y2 } = linearLine(g.angle, W, H);
  return <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={x1} y1={y1} x2={x2} y2={y2} gradientTransform={transform}>{stops}</linearGradient>;
};

/** Short human label ("Linear 90°, 3 stops"). */
export const describeGradient = (gradient) => {
  const g = normalizeGradient(gradient);
  const t = GRADIENT_TYPES.find((x) => x.id === g.type)?.label || 'Linear';
  return `${t}${g.type !== 'radial' ? ` ${Math.round(g.angle)}°` : ''} · ${g.stops.length} stops`;
};
